import {
  AbsoluteCenter,
  Box,
  Button,
  EmptyState,
  HStack,
  Icon,
  IconButton,
  Portal,
  Select,
  Spacer,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { FaLongArrowAltRight, FaTimes } from 'react-icons/fa';
import {
  FaAngleDown,
  FaArrowTrendDown,
  FaArrowTrendUp,
  FaCheck,
  FaPlus,
  FaRegCalendar,
  FaTrash,
} from 'react-icons/fa6';
import { GoGoal } from 'react-icons/go';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { LuPencilLine } from 'react-icons/lu';

import { IndicatorYearSettingPublic, IndicatorsService, IndicatorsYearSettingsService } from '@/client/index.ts';
import Loader from '@/components/Loader.tsx';
import { CTag } from '@/components/common/CTag.tsx';
import { Pagination } from '@/components/common/Pagination.tsx';
import { ResultNotFound } from '@/components/common/ResultNotFound.tsx';
import useCustomToast from '@/hooks/use-custom-toast.ts';
import { Route } from '@/routes/_layout/$slug/indicators/per-year.tsx';

import { trajectory } from '../index/IndicatorData.tsx';
import IndicatorDrawer from '../index/drawer/IndicatorDrawer.tsx';
import { ManageCollecteDeleteDialog } from './ManageCollecteDeleteDialog.tsx';
import ManageCollecteUpdateDrawer from './ManageCollecteUpdateDrawer.tsx';

function getIndicatorsQueryOptions({
  slug,
  page,
  pageSize,
  filter,
  sort,
  search,
  group_by,
}: {
  slug: string;
  page: number;
  pageSize: number;
  filter: string;
  sort: string;
  search: string;
  group_by: string;
}) {
  return {
    queryKey: ['indicators', { page, pageSize, filter, sort, search }],
    queryFn: async () => {
      return await IndicatorsService.readIndicators({
        offset: group_by !== '' ? 1 : (page - 1) * pageSize,
        limit: group_by !== '' ? 9999 : pageSize,
        companySlug: slug,
        filter: filter,
        sort: sort,
        search: search,
      });
    },
  };
}

function getIndicatorsYearSettingsQueryOptions({ slug, indicatorId }: { slug: string; indicatorId: string }) {
  return {
    queryKey: ['indicators_year_settings', { slug, indicatorId }],
    queryFn: async () => {
      return await IndicatorsYearSettingsService.readIndicatorYearSettings({
        companySlug: slug,
        offset: 0,
        limit: 9999,
        indicatorId: indicatorId,
        sort: '-year',
      });
    },
  };
}

export function ManageCollecteTablePerYear() {
  const navigate = useNavigate({ from: Route.fullPath });
  const showToast = useCustomToast();
  const { slug } = useParams({ strict: false });
  const { page, pageSize, filter, sort, search, group_by } = Route.useSearch();
  const [indicatorDetailsId, setIndicatorDetailsId] = useState<string | null>(null);
  const [indicatorAccordionId, setIndicatorAccordionId] = useState<string | null>(null);
  const [indicatorYearSetting, setIndicatorYearSetting] = useState<IndicatorYearSettingPublic | null>(null);
  const [count, setCount] = useState<number>(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [isAddYear, setIsAddYear] = useState<boolean>(false);
  const [addYears, setAddYears] = useState<Array<string>>([]);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);

  const {
    data: indicators,
    isLoading,
    refetch,
  } = useQuery({
    ...getIndicatorsQueryOptions({
      slug,
      page,
      pageSize,
      filter,
      sort,
      search,
      group_by,
    }),
  });

  const { data: indicatorsYearSettings, refetch: refetchIndicatorsYearSettings } = useQuery({
    ...getIndicatorsYearSettingsQueryOptions({
      slug,
      indicatorId: indicatorAccordionId || '',
    }),
    enabled: !!indicatorAccordionId,
  });

  const currentYear = new Date().getFullYear();
  const allYears = Array.from({ length: 21 }, (_, i) => currentYear - 5 + i);

  const existingYears = indicatorsYearSettings?.data.map((year) => year.year) || [];

  const missingYears = allYears.filter((year) => !existingYears.includes(year));

  const yearsNotInstanciate = createListCollection({
    items: missingYears.map((year) => ({
      label: year.toString(),
      value: year,
    })),
  });

  useEffect(() => {
    if (indicators && indicators.count !== undefined) {
      setCount(indicators.count);
    }
  }, [indicators]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    refetch();
  }, [page, filter, pageSize, sort, search]);

  useEffect(() => {
    if (indicators && count < page * pageSize && page > 1) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          page: Math.floor(count / pageSize) + 1,
        }),
      });
    }
  });

  const handleCreateIndicatorYearSettings = async () => {
    if (addYears.length > 0) {
      for (const year of addYears) {
        await IndicatorsYearSettingsService.createIndicatorYearSetting({
          companySlug: slug,
          indicatorId: indicatorAccordionId || '',
          requestBody: { year: +year },
        });
      }
      showToast('Success!', 'Collecte created successfully.', 'success');
    }
    refetchIndicatorsYearSettings();
    setAddYears([]);
    setIsAddYear(false);
  };

  const handleDeleteIndicatorYearSettings = async (indicatorYearSettingId: string) => {
    await IndicatorsYearSettingsService.deleteIndicatorYearSetting({
      companySlug: slug,
      indicatorId: indicatorAccordionId || '',
      id: indicatorYearSettingId,
    });
    showToast('Success!', 'Collecte delete successfully.', 'success');
    setOpenConfirmDelete(false);
    refetchIndicatorsYearSettings();
  };

  const isPaginated =
    count !== 0 && count > pageSize && indicators?.data.length !== undefined && indicators?.data.length < count;

  return (
    <>
      {isLoading ? (
        <Loader loadingText={t('loading.indicators.manage')} />
      ) : (
        <>
          <Box maxH={'calc(83vh - 72px)'} overflowY={'auto'} p={4} pb={'72px'}>
            {indicators?.data.map((item) => (
              <Box key={item.id} boxShadow={'md'} borderRadius={'md'} mb={4} bg={'white'}>
                <HStack
                  p={4}
                  onClick={() => {
                    if (indicatorAccordionId === item.id) {
                      setIndicatorAccordionId(null);
                    } else {
                      setIndicatorAccordionId(item.id ?? '');
                    }
                  }}
                >
                  <VStack alignItems={'flex-start'} gap={1}>
                    <HStack>
                      <Text fontSize="md" fontWeight="bold">
                        {item.name}
                      </Text>
                      <IconButton
                        variant="ghost"
                        aria-label="info"
                        size="2xs"
                        colorPalette="gray"
                        borderRadius={'full'}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIndicatorDetailsId(item.id || '');
                        }}
                      >
                        <HiOutlineInformationCircle />
                      </IconButton>
                    </HStack>
                    <HStack>
                      {item.commitment_name && item.pillar_name && (
                        <CTag label={item.commitment_name ? item.commitment_name : 'N/R'} labelToColor={true} />
                      )}
                      <Text fontSize={'sm'} color={'gray.500'}>
                        {item.commitment_name ? item.commitment_name : 'N/R'}
                      </Text>
                    </HStack>
                  </VStack>
                  <Spacer />
                  <VStack alignItems={'flex-end'} gap={2}>
                    <Text fontSize={'xs'} color={'gray.500'}>
                      {t('indicator.columnsHeader.categoryName')} :{' '}
                      {t('indicator.columnsHeader.category.' + item.category)}
                    </Text>
                    <Text fontSize={'xs'} color={'gray.500'}>
                      {t('indicator.columnsHeader.unit')} : {item.unit}
                    </Text>
                  </VStack>
                  <VStack ml={4}>
                    <motion.div
                      animate={{
                        rotate: indicatorAccordionId === item.id ? 180 : 0,
                      }}
                    >
                      <FaAngleDown />
                    </motion.div>
                  </VStack>
                </HStack>
                <AnimatePresence initial={false}>
                  {indicatorAccordionId === item.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      <VStack px={4} pb={4} gap={4}>
                        {indicatorsYearSettings?.data.map((yearSetting) => (
                          <HStack
                            key={yearSetting.id}
                            border={'1px solid'}
                            borderColor={'primary.200'}
                            borderRadius={'md'}
                            p={4}
                            bg={'gray.100'}
                            w={'full'}
                          >
                            <FaRegCalendar />
                            <VStack alignItems={'flex-start'} gap={1} ml={2}>
                              <Text fontSize="md" fontWeight="bold">
                                {yearSetting.year}
                              </Text>
                              <Text fontSize={'xs'} color={'gray.500'}>
                                Fréquence :{' '}
                                {t(
                                  'indicator.selectValues.collectionFrequency.' + yearSetting.data_collection_frequency
                                )}
                              </Text>
                            </VStack>
                            <Spacer />
                            <GoGoal />
                            <VStack alignItems={'flex-start'} gap={1} ml={2}>
                              <Text fontSize={'sm'} color={'gray.500'}>
                                {t('collection.columnsHeader.objective')}
                              </Text>
                              <Text fontSize="md" fontWeight="bold">
                                {yearSetting.objective || 'N/R'} {item.unit}
                              </Text>
                            </VStack>
                            <Spacer />
                            <VStack alignItems={'flex-start'} gap={1} ml={2}>
                              <Text fontSize={'sm'} color={'gray.500'}>
                                {t('collection.columnsHeader.trajectory')}
                              </Text>
                              {yearSetting.trajectory === null ? (
                                <Text fontSize="md" fontWeight="bold">
                                  N/R
                                </Text>
                              ) : (
                                <HStack
                                  color={
                                    yearSetting.trajectory === 'decrease'
                                      ? 'orange.500'
                                      : yearSetting.trajectory === 'steady'
                                        ? 'yellow.500'
                                        : 'green.500'
                                  }
                                >
                                  <Icon size="md">
                                    <>
                                      {yearSetting.trajectory === 'decrease' && <FaArrowTrendDown />}
                                      {yearSetting.trajectory === 'steady' && <FaLongArrowAltRight />}
                                      {yearSetting.trajectory === 'increase' && <FaArrowTrendUp />}
                                    </>
                                  </Icon>
                                  {trajectory.items.find((item) => item.value === yearSetting.trajectory)?.label}
                                </HStack>
                              )}
                            </VStack>
                            <Spacer />
                            <VStack alignItems={'flex-start'} gap={1} ml={2}>
                              <Text fontSize={'sm'} color={'gray.500'}>
                                {t('collection.columnsHeader.collectedByEntity')}
                              </Text>
                              <CTag
                                alignSelf={'center'}
                                label={
                                  !yearSetting.consolidated ? (
                                    <HStack>
                                      <FaCheck />
                                      Oui
                                    </HStack>
                                  ) : (
                                    <HStack>
                                      <FaTimes />
                                      Non
                                    </HStack>
                                  )
                                }
                                bg={!yearSetting.consolidated ? 'primary.100' : 'red.500'}
                                color={!yearSetting.consolidated ? 'primary.900' : 'white'}
                              />
                            </VStack>
                            <Spacer />
                            <IconButton
                              variant={'ghost'}
                              size="sm"
                              onClick={() => {
                                setOpenEdit(true);
                                setIndicatorYearSetting(yearSetting);
                              }}
                            >
                              <LuPencilLine />
                            </IconButton>
                            <IconButton
                              variant={'ghost'}
                              size="sm"
                              color={{ base: 'red.500', _hover: 'white' }}
                              bg={{ base: 'transparent', _hover: 'red.500' }}
                              onClick={() => {
                                setOpenConfirmDelete(true);
                                setIndicatorYearSetting(yearSetting);
                              }}
                            >
                              <FaTrash />
                            </IconButton>
                          </HStack>
                        ))}
                        {indicatorsYearSettings?.data.length === 0 && (
                          <HStack>
                            <Text fontSize={'sm'} color={'gray.500'}>
                              Aucune collecte pour cet indicateur
                            </Text>
                          </HStack>
                        )}
                        <Box alignSelf={'end'} display={'flex'}>
                          {isAddYear ? (
                            <>
                              <Select.Root
                                multiple
                                size={'sm'}
                                w={'100px'}
                                collection={yearsNotInstanciate}
                                value={addYears}
                                onValueChange={(e) => setAddYears(e.value)}
                              >
                                <Select.HiddenSelect />
                                <Select.Control>
                                  <Select.Trigger>
                                    <Select.ValueText placeholder="Années" />
                                  </Select.Trigger>
                                  <Select.IndicatorGroup>
                                    <Select.Indicator />
                                  </Select.IndicatorGroup>
                                </Select.Control>
                                <Portal>
                                  <Select.Positioner>
                                    <Select.Content>
                                      {yearsNotInstanciate.items.map((year) => (
                                        <Select.Item item={year} key={year.value}>
                                          {year.label}
                                          <Select.ItemIndicator />
                                        </Select.Item>
                                      ))}
                                    </Select.Content>
                                  </Select.Positioner>
                                </Portal>
                              </Select.Root>
                              <IconButton
                                variant="outline"
                                bg="red.500"
                                color="white"
                                size="sm"
                                ml={2}
                                onClick={() => {
                                  setAddYears([]);
                                  setIsAddYear(false);
                                }}
                              >
                                <FaTimes />
                              </IconButton>
                              <IconButton
                                variant="outline"
                                bg="primary"
                                color="white"
                                size="sm"
                                ml={2}
                                onClick={handleCreateIndicatorYearSettings}
                              >
                                <FaCheck />
                              </IconButton>
                            </>
                          ) : (
                            <Button bg={'primary'} size={'sm'} onClick={() => setIsAddYear(true)}>
                              <FaPlus /> Ajouter une collecte
                            </Button>
                          )}
                        </Box>
                      </VStack>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            ))}
          </Box>
          <Box>
            <Pagination isPaginated={isPaginated} count={count} page={page} pageSize={pageSize} navigate={navigate} />
          </Box>
          <IndicatorDrawer
            indicatorId={indicatorDetailsId ?? undefined}
            isOpen={indicatorDetailsId !== null}
            setIsOpen={(state: boolean) => setIndicatorDetailsId(state ? indicatorDetailsId : null)}
            editDisabled={true}
          />
          <ManageCollecteUpdateDrawer
            mode={undefined}
            year={indicatorYearSetting?.year ?? -1}
            open={openEdit}
            selectedIndicators={indicatorYearSetting ? [indicatorYearSetting] : []}
            onUpdate={() => {
              setIndicatorYearSetting(null);
              refetchIndicatorsYearSettings();
            }}
            onOpenChange={(open) => {
              setOpenEdit(open);
              setIndicatorYearSetting(open ? indicatorYearSetting : null);
            }}
          />
          <ManageCollecteDeleteDialog
            open={openConfirmDelete}
            hideTrigger={true}
            onOpenChange={(e) => setOpenConfirmDelete(e)}
            onDelete={() => {
              indicatorYearSetting && handleDeleteIndicatorYearSettings(indicatorYearSetting.id);
            }}
          />
          {count === 0 && filter !== '' && sort !== '' && <ResultNotFound />}
          {count === 0 && filter === '' && sort === '' && (
            <AbsoluteCenter>
              <EmptyState.Root>
                <EmptyState.Content>
                  <VStack textAlign="center">
                    <EmptyState.Title>{t('messages.hints.addIndicator.title')}</EmptyState.Title>
                    <EmptyState.Description>{t('messages.hints.addFirstIndicatorFromManage')}</EmptyState.Description>
                  </VStack>
                </EmptyState.Content>
              </EmptyState.Root>
            </AbsoluteCenter>
          )}
        </>
      )}
    </>
  );
}

export default ManageCollecteTablePerYear;
